import React, { useState } from 'react';
import { usePortalStore } from '../../store';
import { FileText, Edit, Copy, Clock, AlertCircle } from 'lucide-react';

export const DocumentBrowser: React.FC = () => {
  const { documents, selectedDocument, selectDocument } = usePortalStore();
  const [editMode, setEditMode] = useState(false);

  const layers = ['identity', 'constitution', 'governance', 'operational', 'department', 'project'];

  if (!selectedDocument) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-100">Documents</h1>
          <p className="text-slate-400 mt-2">6-layer governance hierarchy</p>
        </div>

        {layers.map(layer => {
          const layerDocs = documents.filter(d => d.layer === layer);
          return (
            <div key={layer} className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-slate-100 mb-4 capitalize">
                {layer.replace('-', ' ')} Layer
              </h2>
              <div className="space-y-2">
                {layerDocs.map(doc => (
                  <button
                    key={doc.id}
                    onClick={() => selectDocument(doc)}
                    className="w-full text-left bg-slate-900 hover:bg-slate-800 rounded-lg p-3 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <FileText className="w-4 h-4 text-cyan-400 mt-1" />
                        <div>
                          <div className="font-semibold text-slate-100">{doc.name}</div>
                          <div className="text-xs text-slate-400 mt-1">Owner: {doc.owner}</div>
                        </div>
                      </div>
                      <div className="text-right text-xs text-slate-400">
                        v{doc.version}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Document detail view
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => selectDocument(null)}
          className="px-4 py-2 hover:bg-slate-700 rounded-lg text-sm transition-colors"
        >
          ← Back
        </button>
        <div className="flex-1 ml-4">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-100">{selectedDocument.name}</h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400 mt-2">
            <span>Owner: {selectedDocument.owner}</span>
            <span>Version: {selectedDocument.version}</span>
            <span>Review: {selectedDocument.reviewCycle}</span>
          </div>
        </div>
        <button
          onClick={() => setEditMode(!editMode)}
          className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
        >
          <Edit className="w-5 h-5 text-slate-400" />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Document Content */}
        <div className="lg:col-span-2 bg-slate-800 border border-slate-700 rounded-lg p-6">
          {editMode ? (
            <textarea
              defaultValue={selectedDocument.content}
              className="w-full h-96 px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-sm text-slate-100 font-mono focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          ) : (
            <div className="prose prose-invert max-w-none text-sm text-slate-300">
              {selectedDocument.content.split('\n').map((line, idx) => (
                <div key={idx} className="mb-2">
                  {line}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Metadata & Relations */}
        <div className="space-y-4">
          {/* Status */}
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-100 mb-3">Metadata</h3>
            <div className="space-y-2 text-sm">
              <div>
                <div className="text-xs text-slate-400">Last Review</div>
                <div className="text-slate-200">{selectedDocument.lastReview.toLocaleDateString()}</div>
              </div>
              <div>
                <div className="text-xs text-slate-400">Review Cycle</div>
                <div className="text-slate-200">{selectedDocument.reviewCycle}</div>
              </div>
              <div>
                <div className="text-xs text-slate-400">Version</div>
                <div className="text-slate-200">{selectedDocument.version}</div>
              </div>
            </div>
          </div>

          {/* Referenced By */}
          {selectedDocument.referencedBy.length > 0 && (
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-slate-100 mb-3">Referenced By</h3>
              <div className="space-y-2">
                {selectedDocument.referencedBy.map(ref => (
                  <button
                    key={ref}
                    className="w-full text-left px-3 py-2 bg-slate-900 hover:bg-slate-700 rounded text-sm text-slate-300 transition-colors"
                  >
                    {ref}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Cascades To */}
          {selectedDocument.cascadesTo.length > 0 && (
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
              <div className="flex items-start gap-2 mb-3">
                <AlertCircle className="w-4 h-4 text-yellow-500 mt-0.5" />
                <h3 className="text-sm font-semibold text-slate-100">Cascade Impact</h3>
              </div>
              <p className="text-xs text-slate-400 mb-3">
                Changing this document may require updating:
              </p>
              <div className="space-y-2">
                {selectedDocument.cascadesTo.map(doc => (
                  <div
                    key={doc}
                    className="px-3 py-2 bg-yellow-900/20 border border-yellow-700 rounded text-sm text-yellow-200"
                  >
                    {doc}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Version History */}
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-100 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Version History
            </h3>
            <div className="space-y-2">
              {[selectedDocument.version, selectedDocument.version - 1, selectedDocument.version - 2].map(v => (
                <button
                  key={v}
                  className="w-full text-left px-3 py-2 bg-slate-900 hover:bg-slate-700 rounded text-sm text-slate-300 transition-colors"
                >
                  v{v}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
